import { computed, toValue } from 'vue'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AgentInterface } from 'models'

/**
 * Resolves the greeting shown in the fullscreen empty state.
 *
 * v1 behavior (docs/specs/released/public-chat-page.md): uses the first entry
 * of the `agentInterface.greeting` pool and supports only the `{name}`
 * placeholder. Without one, the default welcome message. The agent's
 * `initialMessage` is not a fallback: it is written as a first reply, and at
 * the size of a heading a long one filled a phone screen.
 * Pool rotation and `{timeGreeting}` are planned follow-ups.
 */
export function useGreeting(
    agentInterface: MaybeRefOrGetter<AgentInterface | undefined>,
    userName: MaybeRefOrGetter<string | undefined>,
): ComputedRef<string> {
    const { locale, t, te } = useI18n({ useScope: 'global' })

    return computed(() => {
        const ui = toValue(agentInterface)
        const greeting = resolveNamePlaceholder(
            ui?.greeting?.[0]?.[locale.value] ?? '',
            toValue(userName),
        )
        // A greeting of blanks, or of a {name} with no name to put there,
        // leaves nothing to say: the default says it
        return (
            greeting ||
            (te('message.defaultAgentWelcome')
                ? t('message.defaultAgentWelcome')
                : '')
        )
    })
}

function resolveNamePlaceholder(template: string, name?: string): string {
    const result = name
        ? template.replace(/\{name\}/g, name)
        : // No name available: drop the placeholder along with any
          // adjacent punctuation (", {name}!" → "")
          template.replace(/[,\s]*\{name\}[!?]?/g, '')
    return result.trim()
}
